import React, { createRef } from 'react';
import { persistor, store } from './app/redux/store';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider } from 'react-redux';
import AppRoutes from './app/routes/AppRoutes';
import ThemeProvider from "./theme/index"
import axios from 'axios'
import { SnackbarProvider } from 'notistack';
import { GlobalStyles, IconButton, Slide } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { PageTitleProvider } from './app/components/PageTitleContext';
import { SidebarToggleProvider } from './app/common/SidebarToggleContext'
import { KycCountsProvider } from './app/common/KycCountsContext';

axios.interceptors.request.use(
  (config) => {
    const { user: { userInfo: { accessToken } } } = store.getState()
    // if (accessToken && !config.headers.clickPostLogs) {
      if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  (err) => Promise.reject(err)
)

// ─── Snackbar polish ──────────────────────────────────────────────────────────
// One-place customisation that lifts every existing `enqueueSnackbar(...)` call
// (~1.3k of them) to a modern look — brand colors, icons, soft shadow, smooth
// slide animation, per-snack close button. No call-site changes required.
const SNACKBAR_COLORS = {
  success: { bg: '#16a34a', text: '#ffffff' },
  error:   { bg: '#dc2626', text: '#ffffff' },
  warning: { bg: '#d97706', text: '#ffffff' },
  info:    { bg: '#2563eb', text: '#ffffff' },
  default: { bg: '#1e293b', text: '#ffffff' },
};

const snackbarGlobalStyles = (
  <GlobalStyles
    styles={{
      // Shared shell — applies to every variant.
      '.SnackbarItem-message': {
        fontSize: '13.5px !important',
        fontWeight: '500 !important',
        padding: '4px 0 !important',
        gap: '8px',
      },
      '.SnackbarItem-action': {
        marginRight: '-4px !important',
        paddingLeft: '8px !important',
      },
      '.SnackbarContent-root': {
        minWidth: '320px !important',
        maxWidth: '480px',
        borderRadius: '10px !important',
        padding: '8px 14px !important',
        boxShadow: '0 12px 28px -6px rgba(0,0,0,0.18), 0 4px 8px rgba(0,0,0,0.08) !important',
        flexWrap: 'nowrap !important',
      },
      // Variant colors.
      '.SnackbarItem-variantSuccess': { backgroundColor: `${SNACKBAR_COLORS.success.bg} !important`, color: SNACKBAR_COLORS.success.text },
      '.SnackbarItem-variantError':   { backgroundColor: `${SNACKBAR_COLORS.error.bg} !important`,   color: SNACKBAR_COLORS.error.text },
      '.SnackbarItem-variantWarning': { backgroundColor: `${SNACKBAR_COLORS.warning.bg} !important`, color: SNACKBAR_COLORS.warning.text },
      '.SnackbarItem-variantInfo':    { backgroundColor: `${SNACKBAR_COLORS.info.bg} !important`,    color: SNACKBAR_COLORS.info.text },
    }}
  />
);

// Refs for programmatic dismiss from the close button.
const snackbarRef = createRef();
const onDismissClick = (key) => () => snackbarRef.current?.closeSnackbar(key);

const snackbarIconVariants = {
  success: <CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />,
  error:   <ErrorOutlineOutlinedIcon sx={{ fontSize: 20 }} />,
  warning: <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />,
  info:    <InfoOutlinedIcon sx={{ fontSize: 20 }} />,
};

const renderSnackbarAction = (key) => (
  <IconButton
    size="small"
    onClick={onDismissClick(key)}
    sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
    aria-label="Dismiss notification"
  >
    <CloseIcon sx={{ fontSize: 18 }} />
  </IconButton>
);

export default function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider>
          {snackbarGlobalStyles}
          <SnackbarProvider
            ref={snackbarRef}
            autoHideDuration={5000}
            maxSnack={3}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            TransitionComponent={Slide}
            iconVariant={snackbarIconVariants}
            action={renderSnackbarAction}
          >
            <PageTitleProvider>
              <SidebarToggleProvider>
                <KycCountsProvider>
                  <AppRoutes />
                </KycCountsProvider>
              </SidebarToggleProvider>
            </PageTitleProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider >
  );
}